/**
 * Utility functions for organization-specific data handling
 */

/**
 * Get the current organization ID from session storage
 * @returns {string|null} Organization ID or null if not found
 */
export const getCurrentOrganizationId = () => {
    return sessionStorage.getItem('organization_id');
};

/**
 * Get the current user ID from session storage
 * @returns {string|null} User ID or null if not found
 */
export const getCurrentUserId = () => {
    return sessionStorage.getItem('user_id');
};

/**
 * Check if the current user is an organization admin
 * @returns {boolean} True if user is an organization admin
 */
export const isOrganizationAdmin = () => {
    const organizationId = getCurrentOrganizationId();
    const userId = getCurrentUserId();
    return !!(organizationId && userId);
};

/**
 * Check if the current user belongs to an organization
 * @returns {boolean} True if user belongs to an organization
 */
export const isOrganizationMember = () => {
    const organizationId = getCurrentOrganizationId();
    return !!organizationId;
};

/**
 * Build URL parameters for organization-specific API calls
 * @param {Object} additionalParams - Additional parameters to include
 * @returns {URLSearchParams} Configured URL parameters
 */
export const buildOrganizationParams = (additionalParams = {}) => {
    const params = new URLSearchParams();
    
    const organizationId = getCurrentOrganizationId();
    const userId = getCurrentUserId();
    
    if (userId) {
        params.append('user_id', userId);
    }
    
    if (organizationId) {
        params.append('organization_id', organizationId);
    }
    
    // Add any additional parameters
    Object.entries(additionalParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            params.append(key, value);
        }
    });
    
    return params;
};

/**
 * Build API URL with organization parameters
 * @param {string} baseUrl - Base API URL
 * @param {Object} additionalParams - Additional parameters
 * @returns {string} Complete URL with parameters
 */
export const buildOrganizationUrl = (baseUrl, additionalParams = {}) => {
    const params = buildOrganizationParams(additionalParams);
    const paramString = params.toString();
    return paramString ? `${baseUrl}?${paramString}` : baseUrl;
};

/**
 * Check if user has access to organization features
 * @returns {boolean} True if user has organization access
 */
export const hasOrganizationAccess = () => {
    return !!getCurrentOrganizationId();
};

/**
 * Get organization context for API calls
 * @returns {Object} Organization context information
 */
export const getOrganizationContext = () => {
    return {
        organizationId: getCurrentOrganizationId(),
        userId: getCurrentUserId(),
        isAdmin: isOrganizationAdmin(),
        hasAccess: hasOrganizationAccess()
    };
};

/**
 * Validate organization access and show appropriate error
 * @param {Function} setError - Error setter function
 * @returns {boolean} True if access is valid
 */
export const validateOrganizationAccess = (setError) => {
    const organizationId = getCurrentOrganizationId();
    const userId = getCurrentUserId();
    
    if (!userId) {
        setError('Please log in to continue.');
        return false;
    }
    
    if (!organizationId) {
        setError('Organization not found. Please contact your administrator.');
        return false;
    }
    
    return true;
};

/**
 * Handle organization-specific errors
 * @param {Error} error - Error object
 * @param {Function} setError - Error setter function
 */
export const handleOrganizationError = (error, setError) => {
    console.error('Organization API error:', error);
    
    if (error.response) {
        switch (error.response.status) {
            case 401:
                setError('Access denied. Please log in again.');
                break;
            case 403:
                setError('You do not have permission to access this organization.');
                break;
            case 404:
                setError('Organization not found.');
                break;
            default:
                setError(error.response.data?.message || 'An error occurred. Please try again.');
        }
    } else if (error.request) {
        setError('Network error. Please check your connection.');
    } else {
        setError('An unexpected error occurred. Please try again.');
    }
};
